import { API_BASE } from '@/lib/api';

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (
    el: HTMLElement | string,
    options: Record<string, unknown>,
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  execute: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;
let siteKeyPromise: Promise<string> | null = null;

/** Load Turnstile api.js (explicit render). Wait for onload — do not call turnstile.ready(). */
export function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile unavailable'));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const done = () => {
      if (window.turnstile) resolve();
      else reject(new Error('Turnstile unavailable'));
    };
    const fail = () => reject(new Error('Turnstile script failed'));

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-zg-turnstile]',
    );
    if (existing) {
      if (window.turnstile) {
        resolve();
        return;
      }
      existing.addEventListener('load', done, { once: true });
      existing.addEventListener('error', fail, { once: true });
      let tries = 0;
      const poll = () => {
        if (window.turnstile) {
          done();
          return;
        }
        if (tries++ < 40) {
          window.setTimeout(poll, 50);
          return;
        }
        fail();
      };
      poll();
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.dataset.zgTurnstile = '1';
    script.onload = () => {
      script.dataset.loaded = '1';
      done();
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });

  scriptPromise = scriptPromise.catch((err) => {
    scriptPromise = null;
    throw err;
  });

  return scriptPromise;
}

/** Public site key from build env or GET /auth/config. Empty when unset (local). */
export async function getTurnstileSiteKey(): Promise<string> {
  if (import.meta.env.VITE_TURNSTILE_SITE_KEY) {
    return String(import.meta.env.VITE_TURNSTILE_SITE_KEY).trim();
  }
  if (siteKeyPromise) return siteKeyPromise;

  siteKeyPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/config`);
      if (!res.ok) return '';
      const data = (await res.json()) as { turnstileSiteKey?: unknown };
      return typeof data.turnstileSiteKey === 'string'
        ? data.turnstileSiteKey.trim()
        : '';
    } catch {
      return '';
    }
  })();

  return siteKeyPromise;
}

export function getTurnstileApi(): TurnstileApi | undefined {
  return typeof window !== 'undefined' ? window.turnstile : undefined;
}

/**
 * Run an invisible Turnstile challenge (one-shot). Returns '' when no site key
 * is configured so local/dev can skip when the server also skips verification.
 */
export async function getTurnstileToken(action = 'contact'): Promise<string> {
  const sitekey = await getTurnstileSiteKey();
  if (!sitekey) return '';

  await loadTurnstileScript();
  const api = window.turnstile;
  if (!api) throw new Error('Turnstile unavailable');

  return new Promise((resolve, reject) => {
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText =
      'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';
    document.body.appendChild(host);

    let widgetId = '';
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (widgetId) api.remove(widgetId);
      } catch {
        /* ignore */
      }
      host.remove();
      fn();
    };

    const timer = window.setTimeout(() => {
      finish(() => reject(new Error('Turnstile timeout')));
    }, 25_000);

    try {
      widgetId = api.render(host, {
        sitekey,
        size: 'invisible',
        action,
        callback: (token: string) => {
          if (!token) {
            finish(() => reject(new Error('Turnstile empty token')));
            return;
          }
          finish(() => resolve(token));
        },
        'error-callback': (code?: string) => {
          finish(() =>
            reject(
              new Error(
                code ? `Turnstile failed (${code})` : 'Turnstile failed',
              ),
            ),
          );
        },
        'expired-callback': () => {
          finish(() => reject(new Error('Turnstile expired')));
        },
      });
    } catch (e) {
      finish(() =>
        reject(e instanceof Error ? e : new Error('Turnstile failed')),
      );
    }
  });
}
